/* 🔴 F15 STEP 3 — "IT IS LIKE X BUT …" (docs/F15-COACH-ENGINE-DESIGN.md §2.3, §3.2, 2026-10-30).
 *
 * THE THREE QUESTIONS, as data. (1) "Is this one of ours?" — the catalogue link (`catalogLink.ts`) answers it silently for
 * tier 1/2 and asks ONCE for tier 3; any row of ours or of the coach's own may be named instead ("It's like …"). (2) "What's
 * different?" — CHIPS, each writing the engine columns it names and nothing else; a chip the coach never touches inherits
 * its columns BY COPY from the neighbour. (3) READY — `engineAbsentFields` (GENERATED from the readers, step 1) over the
 * values this form would write: nothing absent = "Ready for the engine", else the one line of what is missing.
 *
 * ⚠️ THE COLUMNS ARE THE CATALOGUE'S OWN NAMES (`exercises.difficulty_level`, `.equipment`, …), not §3.2's sketch
 * (`difficulty`, `equipment_canonical`): one vocabulary, so a copy is a plain column-for-column read and `engineAbsentFields`
 * judges the engine row exactly as it judges a catalogue row (codex `vocabulary-mismatch`).
 *
 * ⚠️ AN ABSENT VALUE IS NOT A VALUE: a neighbour that lacks a column (43 published rows have no `movement_pattern_id`,
 * MEASURED 10-30) hands that absence on, and the row stays unready until the coach answers that chip. Nothing defaults. */
import { ENGINE_REQUIRED_FIELDS, engineAbsentFields } from "./engineRequiredFields.generated";
import { CANONICAL_MUSCLES } from "./muscles";
import { isCustomKey } from "./equipmentLibrary";
import { resolve, type CatalogRow, type Link } from "./catalogLink";
import { Constants, type Database } from "./database.types";

type Enums = Database["public"]["Enums"];

/** The engine row — `coach_exercise_engine`'s answer columns, null = not answered. */
export type EngineValues = {
  movement_pattern_id: string | null;
  movement_pattern: string | null;
  primary_muscles: string[] | null;
  difficulty_level: number | null;
  equipment: string[] | null;
  category: string | null;
  exercise_type: string | null;
  is_hold: boolean | null;
  goals_served: string[] | null;
  injury_contraindications: Record<string, string[]> | null;
  mobility_drill_kind: string | null;
  is_unilateral: boolean | null;
};
export type EngineColumn = keyof EngineValues;

export const ENGINE_COLUMNS: readonly EngineColumn[] = [
  "movement_pattern_id", "movement_pattern", "primary_muscles", "difficulty_level", "equipment", "category",
  "exercise_type", "is_hold", "goals_served", "injury_contraindications", "mobility_drill_kind", "is_unilateral",
];

export const EMPTY_ENGINE: EngineValues = {
  movement_pattern_id: null, movement_pattern: null, primary_muscles: null, difficulty_level: null, equipment: null,
  category: null, exercise_type: null, is_hold: null, goals_served: null, injury_contraindications: null,
  mobility_drill_kind: null, is_unilateral: null,
};

/** The one optional column a chip writes (§3.3 `is_unilateral` — "one side" in §2.3's list). Every other chip column is
 *  REQUIRED; `engineDelta.test.ts` holds the chips to `ENGINE_REQUIRED_FIELDS` both ways. */
export const OPTIONAL_CHIP_COLUMNS: readonly EngineColumn[] = ["is_unilateral"];

export type ChipKey = "hard" | "movement" | "kit" | "side" | "hold" | "for" | "kind" | "unsafe" | "muscles";
export type Chip = { key: ChipKey; label: string; writes: readonly EngineColumn[]; needWord: string };

/** "What's different?" — in the order a coach thinks it (§2.3). `needWord` is the word READY's line uses. */
export const CHIPS: readonly Chip[] = [
  { key: "hard", label: "How hard", writes: ["difficulty_level"], needWord: "how hard" },
  { key: "movement", label: "Movement", writes: ["movement_pattern_id", "movement_pattern"], needWord: "movement" },
  { key: "kit", label: "Kit", writes: ["equipment"], needWord: "kit" },
  { key: "side", label: "One side", writes: ["is_unilateral"], needWord: "one side" },
  { key: "hold", label: "Hold or reps", writes: ["is_hold"], needWord: "hold or reps" },
  { key: "for", label: "What it's for", writes: ["goals_served"], needWord: "what it's for" },
  { key: "kind", label: "Kind", writes: ["category", "exercise_type", "mobility_drill_kind"], needWord: "kind" },
  { key: "unsafe", label: "Unsafe for", writes: ["injury_contraindications"], needWord: "unsafe for" },
  { key: "muscles", label: "Muscles", writes: ["primary_muscles"], needWord: "muscles" },
];

export function chipFor(column: string): Chip | undefined {
  return CHIPS.find((c) => (c.writes as readonly string[]).includes(column));
}

/* ── The vocabularies a chip offers — each the engine's own, pinned by the harness ruler `f15-3-like-x-but`
      (Swift enums / the live catalogue) and by the table's CHECKs, so a chip cannot store a word nothing reads. ── */

/** `movement_pattern_id` — the nine ids of the live catalogue (MEASURED 10-30), with the legacy group each one implies. */
export const MOVEMENT_IDS: readonly { id: string; label: string; legacy: string }[] = [
  { id: "h-push", label: "Horizontal push", legacy: "push" },
  { id: "v-push", label: "Vertical push", legacy: "push" },
  { id: "h-pull", label: "Horizontal pull", legacy: "pull" },
  { id: "v-pull", label: "Vertical pull", legacy: "pull" },
  { id: "squat", label: "Squat", legacy: "squat" },
  { id: "hinge", label: "Hinge", legacy: "hinge" },
  { id: "knee-flexion", label: "Knee flexion", legacy: "hinge" },
  { id: "core", label: "Core", legacy: "core" },
  { id: "carry", label: "Carry", legacy: "carry" },
];
/** `exercises.category` — the live enum `exercise_category`, GENERATED into `database.types.ts` (the table's CHECK holds the
 *  same eight; the catalogue's column is text, MEASURED 10-30 to hold exactly these). */
export const ENGINE_CATEGORIES: readonly string[] = Constants.public.Enums.exercise_category;
/** `ExerciseType` (ExerciseEnums.swift) = the Postgres enum `exercise_type`, GENERATED. */
export const EXERCISE_TYPES: readonly string[] = Constants.public.Enums.exercise_type;
/** The legacy group, the Postgres enum `movement_pattern`, GENERATED. */
export const LEGACY_PATTERNS: readonly string[] = Constants.public.Enums.movement_pattern;
/** `MobilityDrillKind` (Exercise.swift), in Smith's session order. */
export const DRILL_KINDS: readonly string[] = ["isolated", "main", "isometric", "contract_relax", "active", "passive"];
/** `TrainingGoal.policySlug` (UserProfile.swift) — the slugs `GoalTagNormalizer` reads a goal tag as. */
export const GOAL_SLUGS: readonly { slug: string; label: string }[] = [
  { slug: "strength", label: "Strength" }, { slug: "hypertrophy", label: "Muscle" }, { slug: "power", label: "Power" },
  { slug: "endurance", label: "Endurance" }, { slug: "fat_loss", label: "Fat loss" }, { slug: "skill", label: "Skill" },
  { slug: "mobility", label: "Mobility" },
];
/** "Unsafe for …" — a joint, stored as every condition the catalogue names for it (MEASURED 10-30, the published rows'
 *  own `injury_contraindications`), so `InjuryMatchingService` refuses the row for any of them. `{}` is the answer "none". */
export const UNSAFE_JOINTS: readonly { joint: string; label: string; conditions: readonly string[] }[] = [
  { joint: "shoulder", label: "Shoulder", conditions: ["Labral Tear", "Rotator Cuff Tear", "Impingement", "AC Joint Sprain"] },
  { joint: "elbow", label: "Elbow", conditions: ["Tendinopathy", "UCL Sprain"] },
  { joint: "wrist", label: "Wrist", conditions: ["Sprain", "Tendinopathy"] },
  { joint: "lower back", label: "Lower back", conditions: ["Disc Herniation", "Acute Strain", "Acute Disc Herniation"] },
  { joint: "knee", label: "Knee", conditions: ["ACL Tear", "Meniscus Tear", "Acute Injury", "Patellar Tendinopathy", "Patellofemoral Pain"] },
  { joint: "ankle", label: "Ankle", conditions: ["Achilles Tendinopathy", "Chronic Instability"] },
];
/** The muscle picker: the GENERATED vocabulary's coarse groups as folders, the fine names inside; the key is stored. */
export const MUSCLE_FOLDERS: readonly { group: string; muscles: readonly { key: string; label: string }[] }[] =
  [...new Set(CANONICAL_MUSCLES.map((m) => m.group))].map((group) => ({
    group, muscles: CANONICAL_MUSCLES.filter((m) => m.group === group).map((m) => ({ key: m.key, label: m.label })),
  }));

/* ── The neighbour, the delta, the result ── */

/** Who the row is "like": a catalogue row (ours) or one of the coach's own READY rows. */
export type Neighbour = { kind: "ours" | "own"; id: string; name: string; values: EngineValues };

/** Only the chips the coach touched, each holding the values of the columns it writes. */
export type Delta = Partial<EngineValues>;

/** The engine row this form would write: the neighbour's values BY COPY, then every touched chip's columns over them. */
export function engineValues(neighbour: EngineValues | null, delta: Delta): EngineValues {
  const base: EngineValues = { ...EMPTY_ENGINE, ...(neighbour ?? {}) };
  const out: EngineValues = { ...base };
  for (const c of ENGINE_COLUMNS) if (c in delta) (out as Record<string, unknown>)[c] = delta[c] ?? null;
  // A drill kind belongs to mobility rows only: a kind chip that moved the row off mobility drops it.
  if (!isMobility(out.category)) out.mobility_drill_kind = null;
  return out;
}

export function isMobility(category: string | null): boolean {
  return (category ?? "").toLowerCase().includes("mobil");
}

/** The REQUIRED columns still absent — the generated rule over the raw row (identity supplied: the coach row has both). */
export function engineMissing(values: EngineValues): string[] {
  const row: Record<string, unknown> = { id: "coach-row", name: "coach-row", ...values };
  const required = new Set(ENGINE_REQUIRED_FIELDS.map((f) => f.column));
  return engineAbsentFields(row).filter((c) => required.has(c));
}

/** A kit the engine cannot read keeps the row off (§3.1: "a custom kit must name its nearest standard kit"). */
export function kitProblem(values: EngineValues): boolean {
  return (values.equipment ?? []).some(isCustomKey);
}

/** Answered columns holding a word outside the generated vocabularies — as good as absent (the table would refuse it). */
export function vocabularyProblems(values: EngineValues): EngineColumn[] {
  const out: EngineColumn[] = [];
  if (values.category !== null && !ENGINE_CATEGORIES.includes(values.category)) out.push("category");
  if (values.exercise_type !== null && !EXERCISE_TYPES.includes(values.exercise_type)) out.push("exercise_type");
  if (values.movement_pattern !== null && !LEGACY_PATTERNS.includes(values.movement_pattern)) out.push("movement_pattern");
  if (values.movement_pattern_id !== null && !MOVEMENT_IDS.some((m) => m.id === values.movement_pattern_id)) out.push("movement_pattern_id");
  if (values.mobility_drill_kind !== null && !DRILL_KINDS.includes(values.mobility_drill_kind)) out.push("mobility_drill_kind");
  return out;
}

/** READY's one line. */
export function readyLine(values: EngineValues): { ready: boolean; text: string } {
  const off = new Set<string>([...engineMissing(values), ...vocabularyProblems(values), ...(kitProblem(values) ? ["equipment"] : [])]);
  // In the chips' order — the order the coach reads them in, one word per chip.
  const words = CHIPS.filter((c) => c.writes.some((w) => off.has(w))).map((c) => c.needWord);
  return words.length === 0 ? { ready: true, text: "Ready for the engine" } : { ready: false, text: `Needs: ${words.join(", ")}` };
}

/** Provenance of a written engine row: a plain copy of our row is `link:<id>` (§3.2); a copy with deltas or from the
 *  coach's own row says whom it was like; no neighbour at all is the coach's own answer. */
export function engineSource(neighbour: Neighbour | null, delta: Delta): string {
  if (neighbour === null) return "coach";
  const touched = Object.keys(delta).length > 0;
  if (neighbour.kind === "ours" && !touched) return `link:${neighbour.id}`;
  return `like:${neighbour.kind}:${neighbour.id}`;
}

/** The neighbour a `source` names, if any — so an opened row shows whom it was answered from. */
export function neighbourFromSource(source: string | null): { kind: "ours" | "own"; id: string } | null {
  if (source === null) return null;
  const link = /^link:(.+)$/.exec(source);
  if (link !== null) return { kind: "ours", id: link[1]! };
  const like = /^like:(ours|own):(.+)$/.exec(source);
  return like !== null ? { kind: like[1] as "ours" | "own", id: like[2]! } : null;
}

/** "How hard" relative to the neighbour: one rung per press, held inside 1–10. */
export function stepDifficulty(current: number | null, by: -1 | 1): number | null {
  if (current === null) return null;
  return Math.min(10, Math.max(1, current + by));
}

/** A joint picked in "Unsafe for" → its catalogue conditions; none picked → `{}` (the answer "none"). */
export function unsafeFor(joints: readonly string[]): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const j of UNSAFE_JOINTS) if (joints.includes(j.joint)) out[j.joint] = [...j.conditions];
  return out;
}

/** The engine row as WRITTEN — it names every column `coach_exercise_engine` owns and not one more (the file's rule, as
 *  `rowFromDraft`). Only called on a READY row: the NOT NULLs would refuse anything else. */
export type EngineWrite = {
  coach_exercise_id: string;
  movement_pattern_id: string;
  movement_pattern: Enums["movement_pattern"];
  primary_muscles: string[];
  difficulty_level: number;
  equipment: string[];
  category: string;
  exercise_type: Enums["exercise_type"];
  is_hold: boolean;
  goals_served: string[];
  injury_contraindications: Record<string, string[]>;
  mobility_drill_kind: string | null;
  is_unilateral: boolean | null;
  source: string;
  engine_use: boolean;
};

export function engineWrite(coachExerciseId: string, values: EngineValues, source: string, engineUse: boolean): EngineWrite | null {
  if (engineMissing(values).length > 0 || kitProblem(values) || vocabularyProblems(values).length > 0) return null;
  return {
    coach_exercise_id: coachExerciseId,
    movement_pattern_id: values.movement_pattern_id!,
    movement_pattern: values.movement_pattern as Enums["movement_pattern"],
    primary_muscles: values.primary_muscles!,
    difficulty_level: values.difficulty_level!,
    equipment: values.equipment!,
    category: values.category!,
    exercise_type: values.exercise_type as Enums["exercise_type"],
    is_hold: values.is_hold!,
    goals_served: values.goals_served!,
    injury_contraindications: values.injury_contraindications!,
    mobility_drill_kind: isMobility(values.category) ? values.mobility_drill_kind : null,
    is_unilateral: values.is_unilateral,
    source,
    engine_use: engineUse,
  };
}

/** A row from `exercises` or `coach_exercise_engine` (the same column names) as values. */
export function valuesFromRow(row: Record<string, unknown>): EngineValues {
  const out: EngineValues = { ...EMPTY_ENGINE };
  for (const c of ENGINE_COLUMNS) {
    const v = row[c];
    (out as Record<string, unknown>)[c] = v === undefined ? null : v;
  }
  return out;
}

/** The columns the editor selects from either source — one list, so the copy cannot miss one. */
export const ENGINE_SELECT = ENGINE_COLUMNS.join(", ");

/* ── Question 1 ── */
export type OursQuestion =
  /** Tier 1/2 or a decided link: answered silently, the neighbour is that row. */
  | { kind: "linked"; id: string }
  /** Tier 3: asked once, "Like our X?" */
  | { kind: "ask"; id: string }
  /** Novel, or a "no": nothing asked; "It's like …" stays offered. */
  | { kind: "none" };

/** Question 1 for a row: a held link is the neighbour, silently; else tier 1/2 answers silently; tier 3 is asked once
 *  (the FIRST candidate, `resolve`'s own order); a novel name asks nothing. */
export function oursQuestion(name: string, held: Link | null, catalogue: readonly CatalogRow[]): OursQuestion {
  if (held !== null) return { kind: "linked", id: held.id };
  const v = resolve(name.trim(), catalogue);
  if (v.kind === "linked") return { kind: "linked", id: v.id };
  if (v.kind === "candidates" && v.ids.length > 0) return { kind: "ask", id: v.ids[0]! };
  return { kind: "none" };
}
